import {
  BONDED_EXPENSE_CODE,
  CASH_EXPENSE_CODE,
  CASH_INCOME_CODE,
} from './booksCategories';
import type { MgaBondedLine, MgaCashEntry, MgaCrewLine } from './useData';

export type MgaCloseCheckStatus = 'pass' | 'fail';

export type MgaCloseCheckItem = {
  id: string;
  status: MgaCloseCheckStatus;
  label: string;
  detail?: string;
  /** Sheet deep-link: payroll | books | notes */
  tab?: 'payroll' | 'books' | 'notes';
  category?: string;
};

export function isOpeningCashEntry(e: Pick<MgaCashEntry, 'description' | 'remarks'>) {
  const hay = `${e.description || ''} ${e.remarks || ''}`.toLowerCase();
  return hay.includes('opening cash') || hay.includes('from closed period');
}

export function isOpeningBondedLine(l: Pick<MgaBondedLine, 'description' | 'remarks' | 'section'>) {
  const hay = `${l.description || ''} ${l.remarks || ''}`.toLowerCase();
  return hay.includes('opening stock') || hay.includes('from prior mga');
}

/** Stock out with amount but no crew payroll charge (store movement only). */
export function isStoreOnlyStockOut(l: MgaBondedLine) {
  if (l.section !== 'EXPENSE') return false;
  const amt = Number(l.amount) || 0;
  if (amt <= 0) return false;
  return !l.payrollColumnCode || !l.mgaCrewLineId;
}

/** Always returns the same checklist rows — each pass (green) or fail (red). */
export function buildMgaCloseChecklist(input: {
  lines: MgaCrewLine[];
  cash: MgaCashEntry[];
  bonded: MgaBondedLine[];
}): MgaCloseCheckItem[] {
  const { lines, cash, bonded } = input;

  const wageGaps = lines.filter((l) => l.missingWageSplit);
  const storeOnly = bonded.filter(isStoreOnlyStockOut);
  const cashBal = cash.reduce((s, e) => {
    const amt = Number(e.amountPeriod ?? e.amount) || 0;
    return e.entryType === 'EXPENSE' ? s - amt : s + amt;
  }, 0);
  const hasIncome = cash.some((e) => e.entryType === 'INCOME' || e.entryType === 'CTM');
  const hasExpense = cash.some((e) => e.entryType === 'EXPENSE');

  const items: MgaCloseCheckItem[] = [
    lines.length > 0
      ? {
          id: 'crew-lines',
          status: 'pass',
          label: `${lines.length} payroll crew line${lines.length === 1 ? '' : 's'}`,
          tab: 'payroll',
        }
      : {
          id: 'crew-lines',
          status: 'fail',
          label: 'No payroll crew lines',
          detail: 'Generate from the vessel crew list first.',
          tab: 'payroll',
        },
    wageGaps.length === 0 && lines.length > 0
      ? {
          id: 'wage-splits',
          status: 'pass',
          label: 'Wage splits complete',
          tab: 'payroll',
        }
      : wageGaps.length > 0
        ? {
            id: 'wage-splits',
            status: 'fail',
            label: `${wageGaps.length} crew line${wageGaps.length === 1 ? '' : 's'} missing wage split`,
            detail: 'Fix on Payroll before close when possible.',
            tab: 'payroll',
          }
        : {
            id: 'wage-splits',
            status: 'fail',
            label: 'Wage splits not checked',
            detail: 'Add payroll crew lines first.',
            tab: 'payroll',
          },
    cashBal >= -0.005
      ? {
          id: 'cash-balance',
          status: 'pass',
          label: 'Cash on hand is not negative',
          tab: 'books',
          category: CASH_INCOME_CODE,
        }
      : {
          id: 'cash-balance',
          status: 'fail',
          label: 'Cash on hand is negative',
          detail: 'Review Income / CTM and Cash out in Books.',
          tab: 'books',
          category: CASH_EXPENSE_CODE,
        },
    storeOnly.length === 0
      ? {
          id: 'stock-out-payroll',
          status: 'pass',
          label: 'Stock outs linked to payroll (or none)',
          tab: 'books',
          category: BONDED_EXPENSE_CODE,
        }
      : {
          id: 'stock-out-payroll',
          status: 'fail',
          label: `${storeOnly.length} Stock out without payroll charge`,
          detail: 'Store-only lines — add crew + charge type if they should hit payroll.',
          tab: 'books',
          category: BONDED_EXPENSE_CODE,
        },
  ];

  if (cash.length === 0) {
    items.push({
      id: 'cash-book',
      status: 'pass',
      label: 'No cash / CTM activity',
      detail: 'OK if the vessel had no cash this period.',
      tab: 'books',
      category: CASH_INCOME_CODE,
    });
  } else if (!hasIncome && hasExpense) {
    items.push({
      id: 'cash-book',
      status: 'fail',
      label: 'Cash out without Income / CTM',
      detail: 'Confirm remittances are recorded.',
      tab: 'books',
      category: CASH_INCOME_CODE,
    });
  } else {
    items.push({
      id: 'cash-book',
      status: 'pass',
      label: 'Cash / CTM book has entries',
      tab: 'books',
      category: CASH_INCOME_CODE,
    });
  }

  return items;
}

export function carryForwardSummary(input: {
  previousBalance?: number | null;
  cash: MgaCashEntry[];
  bonded: MgaBondedLine[];
}) {
  const openingCash = input.cash
    .filter(isOpeningCashEntry)
    .reduce((s, e) => s + (Number(e.amountPeriod ?? e.amount) || 0), 0);
  const openingBonded = input.bonded
    .filter(
      (l) =>
        (l.section === 'STOCK' || l.section === 'INCOMING') && isOpeningBondedLine(l),
    )
    .reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const prev = Number(input.previousBalance) || 0;
  return {
    previousBalance: prev,
    openingCash,
    openingBonded,
    hasAny: Math.abs(prev) > 0.005 || openingCash > 0.005 || openingBonded > 0.005,
  };
}
